import { isEqual, omit } from 'lodash';

import { AugmentedUpdatePresentationBundleInput } from '@/types/schema';

import { ClientPresentationV2Bundle } from '../../types/ClientPresentation.PresentationBundleType';
import { BUNDLE_SAVE_DESCRIPTORS } from '../bundles/registry';
import {
  BundleSaveDescriptor,
  PresentationSaveContext,
} from '../presentationSave.types';
import { canWritePages, diffPages, isEmptyPagesDiff } from './diffPages';

/**
 * Fields handled by the generic differ. `displayName` is derived from bundle
 * configuration and is not written directly.
 */
const GENERIC_BUNDLE_FIELDS = [
  'displayName',
  'id',
  'pages',
  'type',
] as const satisfies readonly (keyof ClientPresentationV2Bundle)[];

/**
 * Browser-only fields that do not require a write. `pendingGenerate` is excluded
 * because only bundle creation can persist it.
 */
const TRANSIENT_BUNDLE_FIELDS = [
  'treeLabelSuffix',
] as const satisfies readonly (keyof ClientPresentationV2Bundle)[];

/** Returns false when any changed field has no writer. */
export function canWriteBundle(
  previous: ClientPresentationV2Bundle,
  next: ClientPresentationV2Bundle
): boolean {
  // The registry is total, so this only misses when the server ships a bundle
  // kind before the client knows it. Nothing about such a bundle can be written.
  const descriptor = BUNDLE_SAVE_DESCRIPTORS[next.type];
  if (!descriptor) return false;
  if (previous.type !== next.type) return false;

  const accountedFor = [
    ...GENERIC_BUNDLE_FIELDS,
    ...TRANSIENT_BUNDLE_FIELDS,
    ...descriptor.ownedFields,
  ];
  if (!isEqual(omit(previous, accountedFor), omit(next, accountedFor))) {
    return false;
  }

  return (
    canWritePages(previous.pages, next.pages) &&
    descriptor.canWrite(previous, next)
  );
}

/**
 * Returns one bundle update, or null when nothing changed. The caller owns
 * `writeIndex` because form state does not retain the persisted bundle index.
 */
export function diffBundle({
  previous,
  next,
  index,
  writeIndex,
  context,
}: {
  previous: ClientPresentationV2Bundle;
  next: ClientPresentationV2Bundle;
  index: number;
  writeIndex: boolean;
  context: PresentationSaveContext;
}): AugmentedUpdatePresentationBundleInput | null {
  const descriptor: BundleSaveDescriptor | undefined =
    BUNDLE_SAVE_DESCRIPTORS[next.type];

  // Reordering does not require a configuration descriptor.
  if (!descriptor) {
    return writeIndex ? { id: next.id, update: { index } } : null;
  }

  const pages = diffPages({
    previous: previous.pages,
    next: next.pages,
    context,
  });
  const configuration = descriptor.diffConfiguration({
    previous,
    next,
    context,
  });
  const { update: configurationUpdate, ...configurationPayload } =
    configuration ?? {};

  if (isEmptyPagesDiff(pages) && !configuration && !writeIndex) {
    return null;
  }

  return {
    id: next.id,
    update: {
      ...configurationUpdate,
      ...(writeIndex ? { index } : {}),
      ...(pages.removePageIDs.length > 0
        ? { removePageIDs: pages.removePageIDs }
        : {}),
    },
    ...(pages.withPages.length > 0 ? { withPages: pages.withPages } : {}),
    ...(pages.updatePages.length > 0 ? { updatePages: pages.updatePages } : {}),
    ...configurationPayload,
  };
}
